/**
 * PublicFormContext
 *
 * Provides organization context for public forms that don't have authentication.
 * This allows data source hooks to know which organization's data to query.
 */

import { createContext, useContext, useMemo, ReactNode } from 'react';
import { idTokenCredential, type AiCredentialResolver } from '../../../lib/forms/aiCredential';

/**
 * Optional per-capture context so the eager upload can stamp Storage provenance
 * (field + capture time + geolocation) on the object — traceable even if the doc
 * reference is later lost. All fields optional; the host fills what it knows.
 */
export interface PhotoUploadContext {
  /** The form field the photo was captured in. */
  fieldKey?: string;
  /** ISO capture time of the photo. */
  capturedAt?: string;
  /** Geolocation captured with the photo, if available. */
  location?: { lat?: number; lng?: number; accuracy?: number } | null;
  /**
   * The photo's own stable client-generated id (round 2 finding #4). When
   * given, the eager upload keys its Storage object by this id instead of a
   * timestamp, so a later queued retry (keyed the same way) overwrites the
   * SAME object instead of orphaning it.
   */
  photoId?: string;
  /**
   * False for a leg that must NEVER become its own queued "photo" on failure
   * (round 2 finding #2) — specifically the thumbnail upload, which shares a
   * photo's id/fieldKey but is not itself a photo entry. Defaults to true.
   */
  allowOfflineQueue?: boolean;
}

/** Uploads a captured blob to Storage at capture time, resolving to its download URL. */
export type UploadPhotoFn = (
  blob: Blob,
  filename: string,
  context?: PhotoUploadContext
) => Promise<string>;

interface PublicFormContextValue {
  /** The organization ID for data source queries */
  organizationId: string | undefined;
  /**
   * The public slug of the form being filled (#3863).
   *
   * getPublicDataSourceOptions no longer accepts a caller-supplied org — it
   * resolves this slug to a template with publicSettings.isPublic === true,
   * takes the org from that template's own path, and serves a preset only if
   * that template declares it. Anonymous hosts (PublicFormPage, EmbedFormPage)
   * must pass it or preset-backed pickers get a 400. Authenticated in-app hosts
   * leave it unset — they query Firestore directly and never hit the endpoint.
   */
  formSlug?: string;
  /**
   * When provided, photo fields upload captured bytes to Storage immediately and
   * store only the resulting download URL — so a mid-fill reload never loses the
   * photo (#2554) and multi-MB base64 never accumulates in memory. Absent for
   * embed/preview contexts, where fields fall back to uploading at submit time.
   */
  uploadPhoto?: UploadPhotoFn;
  /**
   * Reports whether a given photo field currently has an eager upload in flight,
   * so the host (FormFiller) can block submit until uploads settle (avoids a
   * duplicate submit-time re-upload of the same bytes).
   */
  onPhotoUploadingChange?: (fieldKey: string, uploading: boolean) => void;
  /**
   * Forms outbox (plan step B19): save a file's bytes on the phone when its eager
   * upload failed, keyed by the entry's `_localId`. Resolves true only once the
   * bytes are durably stored. Provided only by hosts whose app drains the outbox
   * (the tech app); absent elsewhere, so fields keep warning that the photo lives
   * only in the page.
   */
  keepFileOnDevice?: KeepFileOnDeviceFn;
  /** Forget bytes saved by keepFileOnDevice (the technician removed the photo). */
  dropFileFromDevice?: (localId: string) => void;
  /**
   * Resolves the credential headers for the AI helper endpoints (transcribe,
   * polish, FSR actions). Anonymous public-form hosts inject the capability
   * token minted by getPublicFormTemplate; authenticated hosts leave this unset
   * and fall through to the signed-in user's ID token.
   */
  getAiCredential: AiCredentialResolver;
  /**
   * Show the technician's "suggest for unit gallery" star on captured photos (generator
   * photo gallery U6, D13). On for service reports only; other forms have no unit gallery.
   */
  galleryStars?: boolean;
}

export type KeepFileOnDeviceFn = (file: {
  readonly localId: string;
  readonly blob: Blob;
  readonly fileName: string;
  readonly fieldKey: string;
}) => Promise<boolean>;

const PublicFormContext = createContext<PublicFormContextValue>({
  organizationId: undefined,
  // Default suits every authenticated in-app surface, including those rendered
  // outside a provider (e.g. the SMS compose bar).
  getAiCredential: idTokenCredential,
});

/**
 * Provider for public form context.
 * Wrap PublicFormRenderer with this when rendering a public form.
 */
export function PublicFormProvider({
  organizationId,
  formSlug,
  uploadPhoto,
  onPhotoUploadingChange,
  keepFileOnDevice,
  dropFileFromDevice,
  getAiCredential = idTokenCredential,
  galleryStars = false,
  children,
}: {
  organizationId: string | undefined;
  formSlug?: string;
  uploadPhoto?: UploadPhotoFn;
  onPhotoUploadingChange?: (fieldKey: string, uploading: boolean) => void;
  keepFileOnDevice?: KeepFileOnDeviceFn;
  dropFileFromDevice?: (localId: string) => void;
  getAiCredential?: AiCredentialResolver;
  galleryStars?: boolean;
  children: ReactNode;
}) {
  // Memoized so the context value keeps a stable identity — consumers list
  // getAiCredential in useCallback dependency arrays.
  const value = useMemo(
    () => ({ organizationId, formSlug, uploadPhoto, onPhotoUploadingChange, keepFileOnDevice, dropFileFromDevice, getAiCredential, galleryStars }),
    [organizationId, formSlug, uploadPhoto, onPhotoUploadingChange, keepFileOnDevice, dropFileFromDevice, getAiCredential, galleryStars],
  );

  return <PublicFormContext.Provider value={value}>{children}</PublicFormContext.Provider>;
}

/**
 * Hook to access public form context.
 * Returns organizationId for data source queries.
 */
export function usePublicFormContext(): PublicFormContextValue {
  return useContext(PublicFormContext);
}
